import { useEffect, useState } from "react";
import { Link } from "react-router";
import { listJobs, queueDepth, type JobRow, type QueueDepth } from "../lib/api";

const STATUSES = ["", "queued", "running", "done", "failed", "needs_review"];

export default function Jobs() {
  const [status, setStatus] = useState<string>("");
  const [items, setItems]   = useState<JobRow[]>([]);
  const [depth, setDepth]   = useState<QueueDepth | null>(null);
  const [error, setError]   = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    Promise.all([listJobs({ status: status || undefined, limit: 100 }), queueDepth()])
      .then(([r, d]) => { if (!cancelled) { setItems(r.items); setDepth(d); } })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [status]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <h1 className="text-lg font-semibold">Jobs</h1>
        <select
          value={status}
          onChange={e => setStatus(e.target.value)}
          className="bg-slate-900 border border-slate-800 rounded px-2 py-1 text-sm"
        >
          {STATUSES.map(s => <option key={s} value={s}>{s === "" ? "all statuses" : s}</option>)}
        </select>
        {depth && (
          <div className="ml-auto flex gap-3 text-xs text-slate-400">
            <span>queued: <span className="font-mono text-slate-200">{depth.queued}</span></span>
            <span>running: <span className="font-mono text-slate-200">{depth.running}</span></span>
            <span className={depth.failed ? "text-rainfall-bad" : ""}>failed: <span className="font-mono">{depth.failed}</span></span>
            <span className={depth.needs_review ? "text-rainfall-warn" : ""}>needs review: <span className="font-mono">{depth.needs_review}</span></span>
            <span>done: <span className="font-mono text-slate-200">{depth.done}</span></span>
          </div>
        )}
      </div>

      {error && <div className="text-sm text-rainfall-bad">{error}</div>}

      <div className="overflow-x-auto border border-slate-800 rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-900/60 text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Job</th>
              <th className="px-3 py-2 text-left font-medium">Asset</th>
              <th className="px-3 py-2 text-left font-medium">Status</th>
              <th className="px-3 py-2 text-left font-medium">Requested</th>
              <th className="px-3 py-2 text-left font-medium">Finished</th>
              <th className="px-3 py-2 text-left font-medium">Error</th>
            </tr>
          </thead>
          <tbody>
            {items.map(j => (
              <tr key={j.job_id} className="border-t border-slate-900 hover:bg-slate-900/60">
                <td className="px-3 py-2 font-mono text-xs">{j.job_id.slice(0, 8)}…</td>
                <td className="px-3 py-2 font-mono text-xs">
                  <Link to={`/assets/${j.asset_id}`} className="text-rainfall-accent hover:underline">
                    {j.asset_id.slice(0, 8)}…
                  </Link>
                </td>
                <td className="px-3 py-2"><JobBadge s={j.status} /></td>
                <td className="px-3 py-2 text-xs text-slate-400">{j.created_at}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{j.finished_at ?? "—"}</td>
                <td className="px-3 py-2 text-xs text-rainfall-bad max-w-md truncate" title={j.error ?? ""}>{j.error}</td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500">No jobs.</td></tr>
            )}
          </tbody>
        </table>
        {loading && <div className="px-3 py-3 text-xs text-slate-500">Loading…</div>}
      </div>
    </div>
  );
}

function JobBadge({ s }: { s: string }) {
  const cls = s === "done" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
            : s === "failed" || s === "needs_review" ? "bg-rose-500/15 text-rose-300 border-rose-500/30"
            : s === "running" ? "bg-yellow-500/15 text-yellow-200 border-yellow-500/30"
            : "bg-slate-700/30 text-slate-300 border-slate-600/40";
  return <span className={`text-xs px-2 py-0.5 rounded border ${cls}`}>{s}</span>;
}
